<template>
    <section class="bg-white dark:bg-base-300 dark:shadow-whiteShadow rounded-xl shadow-sm mt-4 p-4 md:p-6"
        aria-labelledby="notes-heading">
        <h4 id="notes-heading" class="text-xl font-medium mb-4">{{ __('Notes') }}</h4>

        <form class="mb-6" @submit.prevent="addNote" novalidate>
            <label :for="textareaId" class="block text-dark-550 dark:text-gray-810 text-sm mb-2 font-medium">
                {{ __('Add note') }}
            </label>
            <textarea :id="textareaId" v-model="content" class="textarea textarea-bordered w-full min-h-28"
                :class="{ 'textarea-error': error }" :placeholder="__('Type your note...')" maxlength="5000"
                :aria-invalid="Boolean(error)" :aria-describedby="error ? errorId : undefined" />
            <p v-if="error" :id="errorId" class="text-error text-sm mt-1" role="alert">{{ error }}</p>
            <div class="flex justify-end mt-3">
                <button type="submit" class="btn btn-primary btn-sm min-w-28" :disabled="saving || !content.trim()">
                    <span v-if="saving" class="loading loading-spinner loading-xs" aria-hidden="true"></span>
                    {{ __('Save') }}
                </button>
            </div>
        </form>

        <ul v-if="localNotes.length" class="space-y-3">
            <li v-for="note in localNotes" :key="note.id"
                class="rounded-lg border border-base-300 dark:border-base-100 p-3 md:p-4">
                <div class="flex items-start justify-between gap-3">
                    <div class="min-w-0">
                        <p class="font-medium text-sm">{{ note.user }}</p>
                        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ note.created_at }}</p>
                    </div>
                    <button v-if="note.self" type="button" class="btn btn-ghost btn-xs text-error shrink-0"
                        :disabled="deletingId === note.id" @click="deleteNote(note)">
                        <span v-if="deletingId === note.id" class="loading loading-spinner loading-xs" aria-hidden="true"></span>
                        {{ __('Delete') }}
                    </button>
                </div>
                <p class="mt-3 whitespace-pre-wrap break-words [overflow-wrap:anywhere] text-sm leading-7">{{ note.content }}</p>
            </li>
        </ul>
        <p v-else class="text-sm text-gray-500 dark:text-gray-400">{{ __('No notes have been recorded yet') }}</p>
    </section>
</template>

<script setup>
import { ref, watch } from 'vue';
import { useToast } from 'vue-toastification';

const props = defineProps({
    noteableType: { type: String, required: true },
    noteableId: { type: [Number, String], required: true },
    notes: { type: Array, default: () => [] },
});

const toast = useToast();
const content = ref('');
const error = ref('');
const saving = ref(false);
const deletingId = ref(null);
const localNotes = ref([]);
const textareaId = `notes-${props.noteableType}-${props.noteableId}`;
const errorId = `${textareaId}-error`;

watch(() => props.notes, (notes) => {
    localNotes.value = [...(notes || [])];
}, { immediate: true, deep: true });

const addNote = async () => {
    const value = content.value.trim();
    error.value = '';
    if (!value) {
        error.value = __('The note text is required');
        return;
    }

    saving.value = true;
    try {
        const response = await axios.post(
            `user/${props.noteableType}/${props.noteableId}/notes`,
            { content: value },
            { suppressValidationToast: true }
        );
        localNotes.value.unshift(response.data.data);
        content.value = '';
        toast.success(response.data.message || __('Note added successfully'));
    } catch (requestError) {
        if (requestError.response?.status === 422)
            error.value = Object.values(requestError.response.data.errors || {}).flat()[0] || __('The note text is required');
        else window.networkError(requestError);
    } finally {
        saving.value = false;
    }
};

const deleteNote = async (note) => {
    if (!window.confirm(__('Are you sure to delete this note?'))) return;

    deletingId.value = note.id;
    try {
        const response = await axios.delete(`user/${props.noteableType}/${props.noteableId}/notes/${note.id}`);
        localNotes.value = localNotes.value.filter((item) => item.id !== note.id);
        toast.success(response.data.message);
    } catch (requestError) {
        window.networkError(requestError);
    } finally {
        deletingId.value = null;
    }
};
</script>
